/**
 * StaticIcon — Unified icon component with stroke↔fill crossfade.
 *
 * Features:
 * - 500ms opacity crossfade between stroke (inactive) and fill (active) variants
 * - Two distinct icon modes:
 *
 *   **Monochrome** (default):
 *     - Inactive: dark gray (`--muted-foreground`, ~#333 light / ~#a0a0a0 dark)
 *     - Active: primary theme color (`--primary`)
 *
 *   **Color**:
 *     - Inactive: per-type color, stroke variant
 *     - Active: per-type color, fill variant (icon keeps its own color;
 *       primary theme color is used only for background highlight by parent)
 *
 * - Accepts standard SVG/HTML props (className, style, etc.)
 */
import { memo } from "react"
import { getIconEntry, type RegisteredIconType } from "./iconRegistry"
import { cn } from "@/shared/utils/general/cn"

export type IconMode = "monochrome" | "color"

interface StaticIconProps {
  /** Registered icon type (e.g. "inbox", "today") */
  type: RegisteredIconType
  /** Whether the icon is in its active/selected state */
  active?: boolean
  /** Icon display size in px (default 24) */
  size?: number
  /** "monochrome" = theme color; "color" = per-type colors */
  mode?: IconMode
  /** When false, active state keeps stroke variant (colored) instead of crossfading to fill.
   *  Useful for area icons that should only change stroke color, not become filled. */
  fillOnActive?: boolean
  /** Additional CSS classes on the wrapper span */
  className?: string
}

/**
 * Renders stroke + fill icon layers with opacity crossfade.
 *
 * @example
 * ```tsx
 * <StaticIcon type="inbox" active={isActive} mode="color" />
 * ```
 */
export const StaticIcon = memo(function StaticIcon({
  type,
  active = false,
  size = 24,
  mode = "monochrome",
  fillOnActive = true,
  className,
}: StaticIconProps) {
  const entry = getIconEntry(type)
  const { stroke: Stroke, fill: Fill, color: typeColor } = entry

  // Color mode: always use per-type color (stroke when inactive, fill when active).
  // Monochrome mode: inactive = muted-foreground (dark gray), active = primary.
  const iconColor =
    mode === "color"
      ? typeColor
      : active
        ? "var(--primary)"
        : "var(--muted-foreground)"

  // When fillOnActive is false, stroke stays visible even when active (just recolored).
  const showFill = fillOnActive && active

  return (
    <span
      className={cn("relative flex shrink-0", className)}
      style={{ width: size, height: size }}
    >
      {/* Stroke layer — visible when inactive (or always when fillOnActive=false) */}
      <Stroke
        width={size}
        height={size}
        className="absolute inset-0 transition-opacity duration-500"
        style={{ color: iconColor, opacity: showFill ? 0 : 1 }}
        aria-hidden
      />
      {/* Fill layer — visible when active (unless fillOnActive=false) */}
      <Fill
        width={size}
        height={size}
        className="absolute inset-0 transition-opacity duration-500"
        style={{ color: iconColor, opacity: showFill ? 1 : 0 }}
        aria-hidden
      />
    </span>
  )
})
